import { Box, Container, Layers3, Package, ShoppingBag, Snowflake, type LucideIcon } from 'lucide-react';
import styled from 'styled-components';
import { ContainerType } from 'data/__generated__';
import { colors } from 'constants/colors';

const getColorsFromType = (type: ContainerType | null) => {
    const { pantinGrey, primaryYellow, green, pureWhite, black, emeraldBlue, blue } = colors;

    switch (type) {
        case ContainerType.Box:
            return { backgroundColor: green, labelColor: pureWhite };
        case ContainerType.Palette:
            return { backgroundColor: blue, labelColor: pureWhite };
        case ContainerType.Kangabox:
            return { backgroundColor: emeraldBlue, labelColor: pureWhite };
        case ContainerType.Parcel:
            return { backgroundColor: pantinGrey, labelColor: black };
        case ContainerType.FreshParcel:
            return { backgroundColor: emeraldBlue, labelColor: pureWhite };
        case ContainerType.Kraft:
            return { backgroundColor: primaryYellow, labelColor: black };
        default:
            return { backgroundColor: pureWhite, labelColor: black };
    }
};

const getLabelFromType = (type: ContainerType | null) => {
    switch (type) {
        case ContainerType.Box:
            return 'Box';
        case ContainerType.Palette:
            return 'Palette';
        case ContainerType.Kangabox:
            return 'Kangabox';
        case ContainerType.Parcel:
            return 'Colis';
        case ContainerType.FreshParcel:
            return 'Colis frais';
        case ContainerType.Kraft:
            return 'Kraft';
        default:
            return '-';
    }
};

/** Le pendant iconographique de `getLabelFromType` : un contenant, un glyphe. */
const CONTAINER_ICONS: Record<ContainerType, LucideIcon> = {
    [ContainerType.Box]: Box,
    [ContainerType.Palette]: Layers3,
    [ContainerType.Kangabox]: Container,
    [ContainerType.Parcel]: Package,
    [ContainerType.FreshParcel]: Snowflake,
    [ContainerType.Kraft]: ShoppingBag,
};

/**
 * La même pastille que `ContainerTypeTag`, repliée sur son icône — mêmes couleurs, le libellé passe en
 * infobulle. Pour les endroits où la place manque, comme l'en-tête d'une tuile de kanban.
 */
export const ContainerTypeIcon = ({ type }: { type: ContainerType }) => {
    const { backgroundColor, labelColor } = getColorsFromType(type);
    const Icon = CONTAINER_ICONS[type];
    return (
        <IconContainer $color={backgroundColor} title={getLabelFromType(type)}>
            <Icon size={13} color={labelColor} />
        </IconContainer>
    );
};

export const ContainerTypeTag = ({ type, customMargin }: { type: ContainerType | null; customMargin?: string }) => {
    const { backgroundColor, labelColor } = getColorsFromType(type);
    return (
        <TagContainer $color={backgroundColor} $customMargin={customMargin}>
            <TagLabel $color={labelColor}>{getLabelFromType(type)}</TagLabel>
        </TagContainer>
    );
};

const TagContainer = styled.div<{ $color: string; $customMargin?: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    border-width: 1px;
    background-color: ${({ $color }: { $color: string }) => $color}dd;
    padding: 5px 15px;
    border-radius: 20px;
    max-width: 150px;
    text-align: center;
    ${({ $customMargin }) => $customMargin && `margin: ${$customMargin}`};
`;

const TagLabel = styled.div<{ $color: string }>`
    color: ${({ $color }) => $color};
    font-size: 14px;
`;

const IconContainer = styled.div<{ $color: string }>`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    background-color: ${({ $color }: { $color: string }) => $color}dd;
`;
